/**
 * MercadoPago Integration Service
 *
 * IMPORTANTE: MercadoPago NO permite gambling en sus TOS.
 * Este sistema se presenta como "venta de fichas de entretenimiento".
 *
 * Requiere:
 * - Backend (API) para manejar las API keys de forma segura
 * - Configurar webhooks para recibir confirmaciones
 */

import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'

declare global {
  interface Window {
    MercadoPago: new (publicKey: string, options?: { locale: string }) => MercadoPagoInstance
  }
}

interface MercadoPagoInstance {
  checkout: (options: {
    preference: { id: string }
    render?: { container: string; label: string }
    callbacks?: {
      onReady?: () => void
      onError?: (error: Error) => void
    }
  }) => void
}

interface MercadoPagoConfig {
  publicKey: string | null
  enabled: boolean
}

interface PaymentPreference {
  preferenceId: string
  initPoint: string
  sandboxInitPoint?: string
}

interface PaymentStatus {
  status: 'approved' | 'pending' | 'rejected' | 'in_process'
  amount?: number
  paymentId?: string
}

class MercadoPagoService {
  private config: MercadoPagoConfig = {
    publicKey: null,
    enabled: false,
  }
  private initialized = false

  async init(): Promise<void> {
    if (this.initialized) return

    try {
      const configDoc = await getDoc(doc(db, 'config', 'mercadopagoApi'))
      if (configDoc.exists()) {
        const data = configDoc.data()
        this.config.publicKey = data.publicKey || null
        this.config.enabled = data.enabled || false
      }

      if (this.config.enabled && this.config.publicKey) {
        await this.loadSDK()
      }

      this.initialized = true
    } catch (error) {
      console.error('Error inicializando MercadoPago:', error)
    }
  }

  private loadSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.MercadoPago) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://sdk.mercadopago.com/js/v2'
      script.onload = () => {
        console.log('MercadoPago SDK cargado')
        resolve()
      }
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  isEnabled(): boolean {
    return this.config.enabled && !!this.config.publicKey
  }

  async createPaymentPreference(
    amount: number,
    userId: string,
    userEmail: string
  ): Promise<PaymentPreference> {
    if (!this.isEnabled()) {
      throw new Error('MercadoPago no está habilitado')
    }

    try {
      const response = await fetch('/api/mercadopago-preference', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          userId,
          userEmail,
          description: `Fichas de entretenimiento - $${amount.toLocaleString()}`,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Error creando preferencia de pago')
      }

      const data = await response.json()
      return {
        preferenceId: data.preferenceId,
        initPoint: data.initPoint,
        sandboxInitPoint: data.sandboxInitPoint,
      }
    } catch (error) {
      console.error('Error creando preferencia MP:', error)
      throw error
    }
  }

  async openCheckout(
    amount: number,
    userId: string,
    userEmail: string
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.isEnabled()) {
      return { success: false, error: 'Pagos automáticos no disponibles' }
    }

    try {
      const preference = await this.createPaymentPreference(amount, userId, userEmail)

      // Redirect to MercadoPago checkout
      const checkoutUrl = preference.initPoint || preference.sandboxInitPoint
      if (checkoutUrl) {
        window.open(checkoutUrl, '_blank')
        return { success: true }
      }

      // Fallback to embedded checkout if SDK is loaded
      if (this.config.publicKey && window.MercadoPago) {
        const mp = new window.MercadoPago(this.config.publicKey, {
          locale: 'es-AR',
        })

        mp.checkout({
          preference: {
            id: preference.preferenceId,
          },
          callbacks: {
            onReady: () => {
              console.log('Checkout MP listo')
            },
            onError: (error) => {
              console.error('Error en checkout MP:', error)
            },
          },
        })
        return { success: true }
      }

      return { success: false, error: 'No se pudo abrir el checkout' }
    } catch (error) {
      console.error('Error abriendo checkout:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error desconocido',
      }
    }
  }

  async checkPaymentStatus(paymentId: string): Promise<PaymentStatus | null> {
    try {
      const response = await fetch(`/api/mercadopago-check?paymentId=${paymentId}`)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error verificando pago:', error)
      return null
    }
  }

  handlePaymentReturn(): { status: string | null; paymentId: string | null } {
    const urlParams = new URLSearchParams(window.location.search)
    const paymentStatus = urlParams.get('payment')
    const paymentId = urlParams.get('payment_id')
    const status = urlParams.get('status')

    if (!paymentStatus && !status) {
      return { status: null, paymentId: null }
    }

    // Clean URL
    window.history.replaceState({}, document.title, window.location.pathname)

    let finalStatus: string | null = null

    if (paymentStatus === 'success' || status === 'approved') {
      finalStatus = 'success'
    } else if (paymentStatus === 'failure' || status === 'rejected') {
      finalStatus = 'failure'
    } else if (paymentStatus === 'pending' || status === 'pending' || status === 'in_process') {
      finalStatus = 'pending'
    }

    return { status: finalStatus, paymentId }
  }
}

export const mercadoPagoService = new MercadoPagoService()
export default mercadoPagoService
